import { useState } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { CalendarDays } from "lucide-react";
import { Modal }          from "@/components/ui/Modal";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency, formatNumber } from "@/utils/format";
import { MonthStat }      from "../chartUtils";
import { PedidosModalContent, ModalEntityTitle } from "./PedidosModalContent";
import { BAR_HOVER_PROPS, lineDots, TOOLTIP_CURSOR_BAND, barRowFromClick } from "@/utils/rechartsInteraction";

/* ─── Tooltip customizado ─────────────────────────────────────────────────── */
interface TooltipProps {
  active?:  boolean;
  payload?: { value: number; name: string; color: string }[];
  label?:   string;
}
function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 min-w-[180px]">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{label}</p>
      {payload.map((item) => (
        <div key={item.name} className="flex items-center justify-between gap-4 text-sm">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
            <span className="text-slate-600">{item.name}</span>
          </span>
          <span className="font-semibold text-slate-800">
            {item.name === "Valor (R$)" ? formatCurrency(item.value) : formatNumber(item.value)}
          </span>
        </div>
      ))}
      <p className="text-[10px] text-slate-400 mt-2">Clique para ver detalhes</p>
    </div>
  );
}

/* ─── Componente ─────────────────────────────────────────────────────────── */
interface Props {
  data:    MonthStat[];
  loading: boolean;
}

export function PedidosPorMes({ data, loading }: Props) {
  const [selected, setSelected] = useState<MonthStat | null>(null);

  return (
    <>
      <ChartContainer
        title="Pedidos por Mês"
        subtitle="Quantidade e valor total de pedidos no período"
        loading={loading}
        empty={!loading && data.length === 0}
        onExpand={() => setSelected(data[data.length - 1] ?? null)}
        height={280}
      >
        <ComposedChart data={data} margin={{ top: 4, right: 56, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis
            dataKey="mes"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            width={28}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            width={52}
            tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" iconSize={8} />
          <Bar
            {...BAR_HOVER_PROPS}
            yAxisId="left"
            dataKey="quantidade"
            name="Qtd. Pedidos"
            fill="#B03858"
            radius={[4, 4, 0, 0]}
            maxBarSize={48}
            cursor="pointer"
            onClick={(item: unknown) => {
              const d = barRowFromClick<MonthStat>(item);
              if (d) setSelected(d);
            }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="valor"
            name="Valor (R$)"
            stroke="#3b82f6"
            strokeWidth={2}
            {...lineDots("#3b82f6")}
          />
        </ComposedChart>
      </ChartContainer>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="lg"
        title={selected && (
          <ModalEntityTitle
            icon={<CalendarDays className="w-4 h-4" />}
            label={selected.mes}
            iconBg="bg-blue-50"
            iconColor="text-blue-600"
          />
        )}
      >
        {selected && (
          <PedidosModalContent pedidos={selected.pedidos} expandable />
        )}
      </Modal>
    </>
  );
}
